import { useEffect, useRef, type PropsWithChildren, type ReactNode, type RefObject } from "react";
import { X } from "lucide-react";

export function Dialog({ open, title, onClose, children, footer, variant, returnFocusRef }: PropsWithChildren<{ open: boolean; title: string; onClose: () => void; footer?: ReactNode; variant?: "quick-add" | "command"; returnFocusRef?: RefObject<HTMLElement | null> }>) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const initialFocus = panel?.querySelector<HTMLElement>("[data-dialog-initial-focus]")
      ?? panel?.querySelector<HTMLElement>("input, textarea, button, [tabindex]");
    initialFocus?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
      if (event.key !== "Tab" || !panel) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>("button, input, textarea, select, [tabindex]:not([tabindex='-1'])")].filter((element) => !element.hasAttribute("disabled"));
      const first = focusable[0]; const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first && last) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last && first) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); (returnFocusRef?.current ?? previous)?.focus(); };
  }, [open, returnFocusRef]);
  if (!open) return null;
  const variantClass = variant ? ` ${variant}` : "";
  return (
    <div className={`dialog-backdrop${variantClass}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`dialog${variantClass}`} role="dialog" aria-modal="true" aria-labelledby="dialog-title" ref={panelRef}>
        <header className="dialog-header"><h2 id="dialog-title">{title}</h2><button className="icon-button" aria-label="Close" onClick={onClose}><X size={18} /></button></header>
        <div className="dialog-body">{children}</div>
        {footer && <footer className="dialog-footer">{footer}</footer>}
      </div>
    </div>
  );
}
